<template>
  <transition name="sheet-fade">
    <div
      v-if="visible"
      class="sheet-backdrop"
      @click.self="onBackdropClick"
    >
      <transition name="sheet-slide" appear>
        <div
          v-if="visible"
          class="sheet"
          role="dialog"
          aria-modal="true"
          :aria-label="ariaLabel"
        >
          <div v-if="dismissible" class="handle" aria-hidden="true"></div>
          <slot />
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
// Chrome das bottom sheets: backdrop, painel, handle, transições de entrada/
// saída, trava de scroll do body e dispensa por toque fora.
//
// Existe porque ~9 componentes repetiam exatamente estas 60 linhas de markup +
// CSS + o watcher de overflow. Cada cópia era uma chance de divergir (e algumas
// já divergiam: nem todas destravavam o scroll no beforeDestroy, o que deixava
// o app travado se a sheet fosse desmontada por navegação em vez de fechada).
//
// O CONTEÚDO vem por slot e é renderizado no escopo do filho — por isso o
// vocabulário visual (título, subtítulo, botões) vive em scss/specs/_sheet.scss
// como classes globais `vxl-sheet__*`, e não aqui.
export default {
  name: 'BaseSheet',
  props: {
    visible: { type: Boolean, default: false },
    // false = o usuário PRECISA decidir (onboarding). Some o handle e o toque
    // fora não fecha.
    dismissible: { type: Boolean, default: true },
    ariaLabel: { type: String, default: null }
  },
  watch: {
    visible: {
      immediate: true,
      handler (now) {
        this.lockBodyScroll(now);
      }
    }
  },
  beforeDestroy () {
    // Desmontada sem fechar (navegação, v-if no pai): sem isto o body fica
    // travado e a tela inteira para de rolar, sem nada visível explicando.
    this.lockBodyScroll(false);
  },
  methods: {
    lockBodyScroll (lock) {
      document.body.style.overflow = lock ? 'hidden' : '';
    },
    onBackdropClick () {
      if (this.dismissible) this.$emit('close');
    }
  }
};
</script>

<style scoped lang="scss">
.sheet-backdrop {
  position: fixed;
  inset: 0;
  background: var(--md-overlay, rgba(0, 0, 0, 0.55));
  display: flex;
  align-items: flex-end;
  justify-content: stretch;
  z-index: 1050;
}

.sheet {
  width: 100%;
  background: var(--md-surface, #131826);
  border-radius: 16px 16px 0 0;
  padding: 8px 16px 24px;
  // Respeita a home indicator do iPhone / barra de navegação do Android.
  padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
  display: flex;
  flex-direction: column;
  gap: 4px;
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.25);
  // Sheet com conteúdo longo (diagnóstico) não pode passar da tela.
  max-height: 90vh;
  overflow-y: auto;
}

.handle {
  width: 36px;
  height: 4px;
  margin: 0 auto 12px;
  background: var(--md-border-strong, rgba(255, 255, 255, 0.2));
  border-radius: 2px;
  flex-shrink: 0;
}

.sheet-fade-enter-active,
.sheet-fade-leave-active {
  transition: opacity 200ms ease;
}
.sheet-fade-enter,
.sheet-fade-leave-to {
  opacity: 0;
}

.sheet-slide-enter-active,
.sheet-slide-leave-active {
  transition: transform 260ms cubic-bezier(0.32, 0.72, 0, 1);
}
.sheet-slide-enter,
.sheet-slide-leave-to {
  transform: translateY(100%);
}
</style>
